

import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import {
  cva,
  type VariantProps,
} from "class-variance-authority"

import { cn } from "@/lib/utils"
import { useAppConfig } from "@/providers/config-provider"
import { getButtonColors } from "@/lib/button-colors"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "shadow [background:var(--btn-primary-bg,hsl(var(--primary)))] [color:var(--btn-primary-text,hsl(var(--primary-foreground)))] hover:[background:var(--btn-primary-hover,hsl(var(--primary)/0.9))]",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border shadow-sm [border-color:var(--btn-primary-bg,hsl(var(--border)))] bg-background [color:var(--btn-primary-bg,hsl(var(--foreground)))] hover:[background:var(--btn-primary-hover,hsl(var(--accent)))] hover:[color:var(--btn-primary-text,hsl(var(--accent-foreground)))]",
        secondary:
          "bg-white shadow-sm [border:1px_solid_var(--btn-primary-bg,hsl(var(--border)))] [color:var(--btn-primary-bg,hsl(var(--foreground)))] hover:bg-secondary/80",
        ghost:
          "[color:var(--btn-primary-bg,hsl(var(--foreground)))] hover:[background:var(--btn-primary-hover,hsl(var(--accent)))] hover:[color:var(--btn-primary-text,hsl(var(--accent-foreground)))]",
        link: "[color:var(--btn-primary-bg,hsl(var(--foreground)))] underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  /**
   * Whether to use API colors for this button
   * @default true
   * Set to false to use default theme colors instead of API branding
   */
  useApiColors?: boolean
}

const Button = React.forwardRef<
  HTMLButtonElement,
  ButtonProps
>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      useApiColors = true,
      style,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button"
    const config = useAppConfig()
    const dynamicColors = useApiColors
      ? getButtonColors(config)
      : {}

    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, className })
        )}
        style={
          {
            ...dynamicColors,
            ...style,
          } as React.CSSProperties
        }
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
